import React, { useState, useEffect, useMemo } from 'react';
import { PropTypes } from 'prop-types';
import { MdCircle } from 'react-icons/md';
import { DialogsProvider, ToastProvider } from '../../providers';
import { useDialogs, useToasts } from '../../hooks';
import { withContextProvider } from '../../hoc';
import Icon from '../../componentsV2/Icons/Icon';
import ContactInfo from '../../componentsV2/Contacts/ContactInfo';
import HeaderIconTooltip from '../../componentsV2/HeaderIconTooltip/HeaderIconTooltip';
import Table from '../../componentsV2/table/Table';
import { capitalizeFirstLetter } from '../../utils/api';
import { redirectToContacts } from '../../utils/navigation';
import { deleteContact, getContact } from '../../utils/api/contact';
import { getPriorityColor, removeContactsFromSite, getH2ANameFromH2AValue } from '../../utils/api/site';
import CommonModal from '../../componentsV2/Common/Modal/Modal';
import Button from '../../componentsV2/Button/Button';
import HeaderWithBreadcrumbs from '../../componentsV2/Headers/HeaderWithBreadcrumbs';
import { getRoleChecks } from '../../utils/roles';
import CustomFieldsForm from '../../componentsV2/FormInputs/CustomFieldsForm';

const ContactShow = ({
  contact, sites = [], currentUserRole, organizationId,
}) => {
  const { isManager, isPlanner } = getRoleChecks(currentUserRole);
  const { showToast } = useToasts();
  const [showDeleteContactModal, setShowDeleteContactModal] = useState(false);
  const [showRemoveSiteModal, setShowRemoveSiteModal] = useState(false);
  const [siteId, setSiteId] = useState();
  const [sitesData, setSitesData] = useState();
  const [currentContact, setCurrentContact] = useState(contact);
  const { openDialog, closeDialog } = useDialogs();

  const breadcrumbLinks = useMemo(() => [
    { href: '/sites', name: 'Sites' },
    { href: '/contacts', name: 'Contacts' },
    { name: currentContact.name },
  ], [currentContact.name]);

  useEffect(() => {
    getContact(organizationId, contact.id)
      .then((response) => response.json())
      .then(data => {
        if (!data.contact_sites) {
          throw new Error(data.error);
        }
        setCurrentContact(data.contact);
        const mapSitesData = data.contact_sites?.map(site => ({
          ...site,
          state: site.state,
          priority: site.priority || 'no_priority',
          h2a: getH2ANameFromH2AValue(site.h2a) || 'No Data',
          county: site.county,
          action: (
            <button type="button" className="focus:outline-none" onClick={() => { setShowRemoveSiteModal(true); setSiteId(site.id); }}>
              <Icon name="delete" />
            </button>
          ),
        }));
        setSitesData(mapSitesData);
      })
      .catch((error) => {
        throw new Error(error);
      });
  }, [organizationId, contact.id]);

  const customizedSort = (sortDetails) => {
    const priorityOrder = {
      no_priority: 0,
      low: 1,
      medium: 2,
      high: 3,
    };

    const h2aOrderAsc = {
      No: 0,
      Yes: 1,
      'No Data': 2,
    };

    const h2aOrderDesc = {
      Yes: 0,
      No: 1,
      'No Data': 2,
    };

    const sortedData = [...sitesData].sort((a, b) => {
      // Extract the raw priority value for comparison
      const aPriority = (a.priority || 'no_priority').toLowerCase();
      const bPriority = (b.priority || 'no_priority').toLowerCase();

      if (sortDetails.field === 'priority') {
        // Priority sorting logic: no_priority, low, medium, and high when in ascending order
        if (sortDetails.order === 'asc') {
          return (priorityOrder[aPriority] || 0) - (priorityOrder[bPriority] || 0);
        }
        return (priorityOrder[bPriority] || 0) - (priorityOrder[aPriority] || 0);
      }

      if (sortDetails.field === 'h2a') {
        // h2a sorting logic: no, yes, and no data when in ascending order; yes, no, and no data when in descending order
        if (sortDetails.order === 'asc') {
          return (h2aOrderAsc[a.h2a] ?? 2) - (h2aOrderAsc[b.h2a] ?? 2);
        }
        return (h2aOrderDesc[a.h2a] ?? 2) - (h2aOrderDesc[b.h2a] ?? 2);
      }

      // Default sorting for other fields (e.g., 'name')
      const fieldA = a[sortDetails.field].toLowerCase();
      const fieldB = b[sortDetails.field].toLowerCase();

      if (fieldA === fieldB) return 0;

      if (sortDetails.order === 'asc') {
        return fieldA > fieldB ? 1 : -1;
      }
      return fieldA < fieldB ? 1 : -1;
    });
    setSitesData(sortedData);
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      className: 'w-64',
      isSortable: true,
    },
    {
      title: 'Priority',
      dataIndex: 'priority',
      className: 'w-52',
      isSortable: true,
    },
    {
      title: 'h2a',
      dataIndex: 'h2a',
      className: 'w-32',
      isSortable: true,
    },
    {
      title: 'county',
      dataIndex: 'county',
      className: 'w-32',
      isSortable: true,
    },
    {
      title: 'state',
      dataIndex: 'state',
      className: 'w-32 text-center',
      isSortable: true,
      headerClassName: 'justify-center',
    },
    {
      title: 'edit',
      dataIndex: 'action',
      className: 'w-32 text-center',
      isSortable: false,
      headerClassName: 'justify-center',
    },
  ];

  const handleDelete = () => {
    deleteContact(contact.id, organizationId)
      .then(resp => {
        if (resp.status === 200) {
          setShowDeleteContactModal(false);
          showToast({ message: 'Contact has been deleted', type: 'success' });
          setTimeout(() => {
            redirectToContacts();
          }, 1000);
        }
      })
      .catch(error => showToast({ message: error.message, type: 'error' }));
  };

  const handleOpenDeleteContactModal = () => {
    openDialog({
      message: [
        <p className="text-center leading-snug">
          Are you sure you want to delete <b>{currentContact.name}</b>?
          This will remove the contact from all of the sites they are associated with.
        </p>,
      ],
      show: true,
      actions: [
        {
          label: 'Cancel',
          variant: 'secondary',
          onClick: closeDialog,
        },
        {
          label: 'Delete contact',
          variant: 'primary',
          onClick: () => {
            handleDelete();
            closeDialog();
          },
        },
      ],
    });
  };

  const handleDeleteSite = (siteIdtoDelete) => {
    const formData = {
      organization_id: contact.organization_id,
      contacts: [
        { id: contact.id },
      ],
    };

    removeContactsFromSite(formData, siteIdtoDelete)
      .then((response) => {
        if (!response.ok) {
          throw new Error(response.error);
        }
        window.location.reload();
      })
      .catch((error) => {
        showToast({ message: error.message, type: 'error' });
      });
  };
  const currentSite = (siteIdtoDelete) => {
    const currSite = sitesData.find(site => site.id === siteIdtoDelete);
    return currSite?.name;
  };

  const handleOpenDeleteRelatedSiteModal = (siteIdToDelete) => {
    openDialog({
      message: [
        <p className="text-center leading-snug">
          Are you sure you want to remove <b>{currentContact.name}</b> from <b>{currentSite(siteIdToDelete)}</b>?
        </p>,
      ],
      show: true,
      actions: [
        {
          label: 'Cancel',
          variant: 'secondary',
          onClick: closeDialog,
        },
        {
          label: 'Remove contact',
          variant: 'primary',
          onClick: () => {
            handleDeleteSite(siteIdToDelete);
            closeDialog();
          },
        },
      ],
    });
  };

  const renderSiteData = () => {
    return sitesData?.map(site => ({
      ...site,
      state: site.state,
      priority: (
        <div className="flex space-x-2 items-center">
          <MdCircle size={16} color={getPriorityColor(site?.priority?.toLowerCase())} />
          <p>{!site.priority || site.priority === 'no_priority' ? 'No Priority' : capitalizeFirstLetter(site.priority)}</p>
        </div>
      ),
      h2a: site.h2a,
      county: site.county,
      action: (
        <button type="button" className="focus:outline-none" onClick={() => { handleOpenDeleteRelatedSiteModal(site.id); }}>
          <Icon name="delete" />
        </button>
      ),
    }));
  };

  return (
    <DialogsProvider>
      <div className="ml-2.75">
        <header>
          <HeaderWithBreadcrumbs links={breadcrumbLinks} name="contact" label="Contacts" size="h-9" shouldNotDisplayHeader />
        </header>
        <div className="w-[80%]">
          <ContactInfo
            organizationId={organizationId}
            contact={currentContact}
            isManager={isManager}
            isPlanner={isPlanner}
            onDelete={() => handleOpenDeleteContactModal()}
            onContactUpdate={(updatedContact) => {
              setCurrentContact(updatedContact);
            }}
            showToast={showToast}
            setCurrentContact={setCurrentContact}
          />
          <CustomFieldsForm
            customFields={(currentContact.custom_fields || []).map((field) => ({
              ...field,
            }))}
            isLabelBeside={false}
            isViewOnly
          />
          <div>
            <HeaderIconTooltip
              heading="Related Sites"
              spanTextStart="Sites related to this Contact will appear below. You can add this Contact to other Sites by going to the page for each Site, and adding the contact there."
              place="right"
              iconName="icon_circle"
              id="relatedSites"
              tooltipId="realted-sites-tooltip"
              className="mt-0"
              margins="mb-4 mt-0"
              border="border-b border-vamos-blue-gray pb-2"
            />
            {sites.length > 0 && (
              <Table
                dataSource={renderSiteData()}
                columns={columns}
                onSort={customizedSort}
                typeOfData="sites"
                defaultSortOrder={{ h2a: 'desc' }}
              />
            )}
          </div>
        </div>
        {showDeleteContactModal
          && (
            <CommonModal isHeaderDivider={false} handleClose={() => setShowDeleteContactModal(false)}>
              <div className="pb-10 pt-15 px-4">
                <span className="block mb-1">Are you sure you want to delete <span className="font-bold">{currentContact.name}</span>?
                  This will remove the contact from all of the sites they are associated with.
                </span>
                <div className="flex space-x-4 justify-center pt-7">
                  <Button label="Cancel" size="md" color="secondary" className="px-6" onClick={() => setShowDeleteContactModal(false)} />
                  <Button label="Delete" size="md" className="px-6" onClick={handleDelete} />
                </div>
              </div>
            </CommonModal>
          )}

        {showRemoveSiteModal
          && (
            <CommonModal isHeaderDivider={false} handleClose={() => setShowRemoveSiteModal(false)}>
              <div className="pb-10 pt-15 px-4">
                <span className="block mb-1">Are you sure you want to remove current contact from this site?
                </span>
                <div className="flex space-x-4 justify-center pt-7">
                  <Button label="Cancel" size="md" color="secondary" className="px-6" onClick={() => setShowRemoveSiteModal(false)} />
                  <Button label="Delete" size="md" className="px-6" onClick={() => { handleDeleteSite(siteId); setShowRemoveSiteModal(false); }} />
                </div>
              </div>
            </CommonModal>
          )}
      </div>
    </DialogsProvider>
  );
};

ContactShow.propTypes = {
  contact: PropTypes.object.isRequired,
  sites: PropTypes.array,
  currentUserRole: PropTypes.string.isRequired,
  organizationId: PropTypes.number.isRequired,
};

export default withContextProvider(DialogsProvider)(withContextProvider(ToastProvider)(ContactShow));
